import { useState, useEffect } from 'react';

/**
 * iOS PWA Detection with Notch/Dynamic Island Detection
 * 
 * Provides comprehensive detection for iOS devices running in PWA standalone mode
 * with specific support for notch and Dynamic Island detection.
 */

export interface IOSPWAInfo {
    // Basic PWA detection
    isStandalone: boolean;
    isIOSDevice: boolean;
    isIOSPWA: boolean;
    
    // Notch/Dynamic Island detection
    hasNotch: boolean;
    hasDynamicIsland: boolean;
    hasTopInset: boolean;
    
    // Safe area measurements
    safeAreaTop: number;
    safeAreaBottom: number;
    safeAreaLeft: number;
    safeAreaRight: number;
    
    // Device model detection
    deviceModel: string;
    iosVersion: string;
    
    // Display characteristics
    statusBarHeight: number;
    isFullscreen: boolean;
}

/**
 * Detect if the app is running in standalone PWA mode
 */
function detectStandaloneMode(): boolean {
    // Method 1: Check navigator.standalone (iOS Safari specific)
    if ('standalone' in window.navigator && (window.navigator as any).standalone) {
        return true;
    }
    
    // Method 2: Check display-mode media query
    if (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) {
        return true;
    }
    
    // Method 3: Check for PWA specific characteristics
    if (window.matchMedia && window.matchMedia('(display-mode: minimal-ui)').matches) {
        return true;
    }
    
    return false;
}

/**
 * Detect iOS device and version
 */
function detectIOSDevice(): { isIOS: boolean; version: string; model: string } {
    const userAgent = navigator.userAgent;
    
    // iOS device detection
    const isIOS = /iPad|iPhone|iPod/.test(userAgent);
    
    if (!isIOS) {
        return { isIOS: false, version: '', model: '' };
    }
    
    // Extract iOS version
    let version = '';
    const versionMatch = userAgent.match(/OS (\d+)_(\d+)_?(\d+)?/);
    if (versionMatch) {
        version = `${versionMatch[1]}.${versionMatch[2]}${versionMatch[3] ? '.' + versionMatch[3] : ''}`;
    }
    
    // Detect device model
    let model = 'iPhone';
    if (/iPad/.test(userAgent)) {
        model = 'iPad';
    } else if (/iPod/.test(userAgent)) {
        model = 'iPod';
    }
    
    return { isIOS, version, model };
}

/**
 * Get safe area inset values using CSS environment variables
 */
function getSafeAreaInsets(): {
    top: number;
    bottom: number;
    left: number;
    right: number;
} {
    // Create a temporary element to measure CSS environment variables
    const testElement = document.createElement('div');
    testElement.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        width: 1px;
        height: 1px;
        padding-top: env(safe-area-inset-top);
        padding-bottom: env(safe-area-inset-bottom);
        padding-left: env(safe-area-inset-left);
        padding-right: env(safe-area-inset-right);
        pointer-events: none;
        visibility: hidden;
    `;
    
    document.body.appendChild(testElement);
    
    const computedStyle = window.getComputedStyle(testElement);
    const top = parseFloat(computedStyle.paddingTop) || 0;
    const bottom = parseFloat(computedStyle.paddingBottom) || 0;
    const left = parseFloat(computedStyle.paddingLeft) || 0;
    const right = parseFloat(computedStyle.paddingRight) || 0;
    
    document.body.removeChild(testElement);
    
    return { top, bottom, left, right };
}

/**
 * Detect notch and Dynamic Island based on safe area insets and device characteristics
 */
function detectNotchAndDynamicIsland(
    safeAreaTop: number,
    deviceModel: string,
    iosVersion: string,
    screenHeight: number,
    screenWidth: number
): {
    hasNotch: boolean;
    hasDynamicIsland: boolean;
    hasTopInset: boolean;
    statusBarHeight: number;
} {
    const hasTopInset = safeAreaTop > 0;
    
    // Dynamic Island detection (iPhone 14 Pro and later)
    // Dynamic Island typically has a safe area inset around 47-59px
    const hasDynamicIsland = (
        hasTopInset &&
        deviceModel === 'iPhone' &&
        safeAreaTop >= 47 && safeAreaTop <= 59 &&
        parseFloat(iosVersion) >= 16.0
    );
    
    // Notch detection (iPhone X and later, but before Dynamic Island)
    // Notch typically has safe area inset around 44-47px
    const hasNotch = (
        hasTopInset &&
        !hasDynamicIsland &&
        deviceModel === 'iPhone' &&
        safeAreaTop >= 20 && safeAreaTop <= 50
    );
    
    // Status bar height estimation
    let statusBarHeight = safeAreaTop;
    if (!hasTopInset) {
        // Fallback for devices without safe area insets
        statusBarHeight = 20; // Standard iOS status bar height
    }
    
    return {
        hasNotch,
        hasDynamicIsland,
        hasTopInset,
        statusBarHeight
    };
}

/**
 * Main iOS PWA detection hook
 */
export function useIOSPWADetection(): IOSPWAInfo {
    const [iosPWAInfo, setIOSPWAInfo] = useState<IOSPWAInfo>(() => {
        // Initial state
        return {
            isStandalone: false,
            isIOSDevice: false,
            isIOSPWA: false,
            hasNotch: false,
            hasDynamicIsland: false,
            hasTopInset: false,
            safeAreaTop: 0,
            safeAreaBottom: 0,
            safeAreaLeft: 0,
            safeAreaRight: 0,
            deviceModel: '',
            iosVersion: '',
            statusBarHeight: 0,
            isFullscreen: false
        };
    });

    useEffect(() => {
        const detectIOSPWA = () => {
            // Basic detection
            const isStandalone = detectStandaloneMode();
            const { isIOS, version, model } = detectIOSDevice();
            const isIOSPWA = isStandalone && isIOS;
            
            // Only proceed with detailed detection if it's iOS PWA
            if (!isIOSPWA) {
                setIOSPWAInfo({
                    isStandalone,
                    isIOSDevice: isIOS,
                    isIOSPWA: false,
                    hasNotch: false,
                    hasDynamicIsland: false,
                    hasTopInset: false,
                    safeAreaTop: 0,
                    safeAreaBottom: 0,
                    safeAreaLeft: 0,
                    safeAreaRight: 0,
                    deviceModel: model,
                    iosVersion: version,
                    statusBarHeight: 0,
                    isFullscreen: false
                });
                return;
            }
            
            // Get safe area insets
            const safeAreas = getSafeAreaInsets();
            
            // Detect notch and Dynamic Island
            const notchDetection = detectNotchAndDynamicIsland(
                safeAreas.top,
                model,
                version,
                window.screen.height,
                window.screen.width
            );
            
            // Check if app is in fullscreen mode
            const isFullscreen = window.innerHeight === window.screen.height;
            
            setIOSPWAInfo({
                isStandalone,
                isIOSDevice: isIOS,
                isIOSPWA,
                hasNotch: notchDetection.hasNotch,
                hasDynamicIsland: notchDetection.hasDynamicIsland,
                hasTopInset: notchDetection.hasTopInset,
                safeAreaTop: safeAreas.top,
                safeAreaBottom: safeAreas.bottom,
                safeAreaLeft: safeAreas.left,
                safeAreaRight: safeAreas.right,
                deviceModel: model,
                iosVersion: version,
                statusBarHeight: notchDetection.statusBarHeight,
                isFullscreen
            });
        };

        // Initial detection
        detectIOSPWA();

        // Re-detect on orientation change (safe area insets can change)
        const handleOrientationChange = () => {
            // Small delay to ensure DOM has updated after orientation change
            setTimeout(detectIOSPWA, 100);
        };

        // Re-detect on window resize
        const handleResize = () => {
            setTimeout(detectIOSPWA, 100);
        };

        window.addEventListener('orientationchange', handleOrientationChange);
        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('orientationchange', handleOrientationChange);
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    return iosPWAInfo;
}

/**
 * Simplified hook that returns true only for iOS PWA mode
 */
export function useIsIOSPWA(): boolean {
    const { isIOSPWA } = useIOSPWADetection();
    return isIOSPWA;
}

/**
 * Hook that returns notch/Dynamic Island detection for iOS PWA only
 */
export function useIOSPWANotchDetection(): {
    hasNotch: boolean;
    hasDynamicIsland: boolean;
    safeAreaTop: number;
    isIOSPWA: boolean;
} {
    const { isIOSPWA, hasNotch, hasDynamicIsland, safeAreaTop } = useIOSPWADetection();
    return {
        hasNotch: isIOSPWA ? hasNotch : false,
        hasDynamicIsland: isIOSPWA ? hasDynamicIsland : false,
        safeAreaTop: isIOSPWA ? safeAreaTop : 0,
        isIOSPWA
    };
}

/**
 * CSS utility class generator for iOS PWA safe areas
 */
export function generateIOSPWASafeAreaCSS(): string {
    return `
        /* iOS PWA Safe Area Classes - only applied in standalone mode */
        @supports (padding-top: env(safe-area-inset-top)) {
            @media (display-mode: standalone) {
                .ios-pwa-safe-top {
                    padding-top: env(safe-area-inset-top);
                }
                
                .ios-pwa-safe-bottom {
                    padding-bottom: env(safe-area-inset-bottom);
                }
                
                .ios-pwa-safe-left {
                    padding-left: env(safe-area-inset-left);
                }
                
                .ios-pwa-safe-right {
                    padding-right: env(safe-area-inset-right);
                }
                
                .ios-pwa-safe-all {
                    padding-top: env(safe-area-inset-top);
                    padding-bottom: env(safe-area-inset-bottom);
                    padding-left: env(safe-area-inset-left);
                    padding-right: env(safe-area-inset-right);
                }
                
                .ios-pwa-margin-top {
                    margin-top: env(safe-area-inset-top);
                }
                
                .ios-pwa-margin-bottom {
                    margin-bottom: env(safe-area-inset-bottom);
                }
            }
        }
    `;
} 